import React, { useState, useCallback, useEffect } from 'react';
import type {
  FormElement,
  FormData,
  FormRendererProps,
  ThemeConfig,
  ComponentMap,
} from '../types';
import { FormValidator } from '../validation/validator';
import { ConditionalLogicEvaluator } from '../conditional-logic/evaluator';
import { ElementRenderer } from './ElementRenderer';

/**
 * Main FormRenderer Component
 *
 * Renders a complete form with validation, conditional logic, and custom styling.
 *
 * @example
 * ```tsx
 * <FormRenderer
 *   formDefinition={form.formDefinition}
 *   customCss={form.customCss}
 *   onSubmit={(data) => console.log('Submit:', data)}
 *   theme={{ colors: { primary: '#007bff' } }}
 * />
 * ```
 */
export const FormRenderer: React.FC<FormRendererProps> = ({
  formDefinition,
  onSubmit,
  onChange,
  initialValues = {},
  theme,
  customComponents,
  customCss,
  showValidation = true,
  disabled = false,
}) => {
  // Form state
  const [formData, setFormData] = useState<FormData>(initialValues);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validators
  const [validator] = useState(() => new FormValidator(formDefinition));
  const [logicEvaluator] = useState(() => new ConditionalLogicEvaluator(formDefinition));

  // Update conditional logic when form data changes
  useEffect(() => {
    logicEvaluator.updateData(formData);
  }, [formData, logicEvaluator]);

  // Inject custom CSS
  useEffect(() => {
    if (customCss) {
      const styleId = 'mediform-custom-css';
      let styleElement = document.getElementById(styleId) as HTMLStyleElement;

      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = styleId;
        document.head.appendChild(styleElement);
      }

      styleElement.textContent = customCss;

      return () => {
        // Clean up on unmount
        if (styleElement && styleElement.parentNode) {
          styleElement.parentNode.removeChild(styleElement);
        }
      };
    }
  }, [customCss]);

  /**
   * Handle field value change
   */
  const handleFieldChange = useCallback(
    (fieldId: string, value: any) => {
      const newData = { ...formData, [fieldId]: value };
      setFormData(newData);

      // Call onChange callback
      if (onChange) {
        onChange(newData);
      }

      // Validate field if it's been touched
      if (touched.has(fieldId) && showValidation) {
        const fieldErrors = validator.validateField(fieldId, value);
        setErrors(prev => ({
          ...prev,
          [fieldId]: fieldErrors,
        }));
      }
    },
    [formData, onChange, touched, showValidation, validator]
  );

  /**
   * Handle field blur (mark as touched)
   */
  const handleFieldBlur = useCallback(
    (fieldId: string) => {
      setTouched(prev => new Set(prev).add(fieldId));

      // Validate field on blur if validation is enabled
      if (showValidation) {
        const value = formData[fieldId];
        const fieldErrors = validator.validateField(fieldId, value);
        setErrors(prev => ({
          ...prev,
          [fieldId]: fieldErrors,
        }));
      }
    },
    [formData, showValidation, validator]
  );

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (disabled || isSubmitting) return;

      setIsSubmitting(true);

      // Validate entire form
      const validationResult = validator.validate(formData);

      if (!validationResult.valid) {
        setErrors(validationResult.errors);
        // Mark all fields with errors as touched
        const touchedFields = new Set(touched);
        Object.keys(validationResult.errors).forEach(fieldId => {
          touchedFields.add(fieldId);
        });
        setTouched(touchedFields);
        setIsSubmitting(false);
        return;
      }

      // Clear errors on successful validation
      setErrors({});

      // Call onSubmit callback
      if (onSubmit) {
        try {
          await onSubmit(formData);
        } catch (error) {
          console.error('Form submission error:', error);
        }
      }

      setIsSubmitting(false);
    },
    [formData, validator, onSubmit, disabled, isSubmitting, touched]
  );

  /**
   * Apply custom styles from element
   */
  const applyCustomStyles = (element: FormElement): React.CSSProperties => {
    const styles: React.CSSProperties = {};

    if (element.properties?.customStyles) {
      element.properties.customStyles.forEach(({ property, value }) => {
        // Convert CSS property names to camelCase for React
        const camelProp = property.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
        (styles as any)[camelProp] = value;
      });
    }

    return styles;
  };

  // Render nothing if no form definition
  if (!formDefinition) {
    return null;
  }

  const rootStyles = applyCustomStyles(formDefinition);
  const formClassName = formDefinition.properties?.className || 'mediform-root';

  return (
    <div
      className={`mediform-container ${formClassName}`}
      style={rootStyles}
      data-theme={theme ? 'custom' : 'default'}
    >
      <form onSubmit={handleSubmit} className="mediform-form" noValidate>
        <ElementRenderer
          element={formDefinition}
          formData={formData}
          errors={errors}
          touched={touched}
          onChange={handleFieldChange}
          onBlur={handleFieldBlur}
          disabled={disabled}
          theme={theme}
          customComponents={customComponents}
          logicEvaluator={logicEvaluator}
        />

        {/* Submit button - only if form has onSubmit handler */}
        {onSubmit && (
          <div className="mediform-submit-container">
            <button
              type="submit"
              className="mediform-submit-button"
              disabled={disabled || isSubmitting}
              style={theme?.colors?.primary ? { backgroundColor: theme.colors.primary } : undefined}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        )}
      </form>

      {/* Inject theme CSS variables */}
      {theme && (
        <style>
          {`
            .mediform-container[data-theme="custom"] {
              ${theme.colors?.primary ? `--mediform-primary: ${theme.colors.primary};` : ''}
              ${theme.colors?.secondary ? `--mediform-secondary: ${theme.colors.secondary};` : ''}
              ${theme.colors?.error ? `--mediform-error: ${theme.colors.error};` : ''}
              ${theme.colors?.success ? `--mediform-success: ${theme.colors.success};` : ''}
              ${theme.colors?.background ? `--mediform-background: ${theme.colors.background};` : ''}
              ${theme.colors?.border ? `--mediform-border: ${theme.colors.border};` : ''}
              ${theme.fonts?.family ? `--mediform-font-family: ${theme.fonts.family};` : ''}
              ${theme.fonts?.size ? `--mediform-font-size: ${theme.fonts.size};` : ''}
              ${theme.spacing?.field ? `--mediform-spacing-field: ${theme.spacing.field};` : ''}
              ${theme.spacing?.section ? `--mediform-spacing-section: ${theme.spacing.section};` : ''}
            }
          `}
        </style>
      )}
    </div>
  );
};

export default FormRenderer;
